import { OperatorType } from 'angular-slickgrid';
import { BaseRangeFilter } from './base-range-filter';
import { getEqualLabelKeyForType } from './utils/range-labels';
import { localizeWithFallback } from '../../utils/localization-utils';

export class CustomNumberRangeFilter extends BaseRangeFilter<number> {
  private equalInputElm!: HTMLInputElement;
  private fromInputElm!: HTMLInputElement;
  private toInputElm!: HTMLInputElement;

  override clear(shouldTriggerQuery = true): void {
    if (this.equalInputElm) {
      this.equalInputElm.value = '';
    }
    if (this.fromInputElm) {
      this.fromInputElm.value = '';
    }
    if (this.toInputElm) {
      this.toInputElm.value = '';
    }

    super.clear(shouldTriggerQuery);
  }

    protected createInputsForMode(mode: string): HTMLElement {
    const container = document.createElement('div');

    if (mode === 'equal') {
      this.equalInputElm = document.createElement('input');
      this.equalInputElm.type = 'text';
      this.equalInputElm.inputMode = 'decimal';
      this.equalInputElm.placeholder = localizeWithFallback(this.localizationService, 'LanguageService::EnterNumber');
      this.equalInputElm.className = 'form-control equal-input';

      const equalLabel = document.createElement('label');
      equalLabel.className = 'input-label';
      equalLabel.textContent =
        localizeWithFallback(this.localizationService, getEqualLabelKeyForType('number')) ||
        getEqualLabelKeyForType('number');

      this.addNumericInputFilter(this.equalInputElm);

      this.equalInputElm.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.applyButtonElm.click();
      });

      this.equalInputElm.addEventListener('input', () => {
        this.toggleInputFilledClass(this.equalInputElm);
        this.updateApplyButtonState();
      });

      container.appendChild(equalLabel);
      container.appendChild(this.equalInputElm);
    } else {
      container.className = 'between-inputs';
      const fromContainer = document.createElement('div');
      const fromLabel = document.createElement('label');
      fromLabel.className = 'input-label';
      fromLabel.textContent = localizeWithFallback(this.localizationService, 'LanguageService::From');
      this.fromInputElm = document.createElement('input');
      this.fromInputElm.type = 'text';
      this.fromInputElm.inputMode = 'decimal';
      this.fromInputElm.placeholder = localizeWithFallback(this.localizationService, 'LanguageService::EnterNumber');
      this.fromInputElm.className = 'form-control from-input';

      this.addNumericInputFilter(this.fromInputElm);

      fromContainer.appendChild(fromLabel);
      fromContainer.appendChild(this.fromInputElm);

      const toContainer = document.createElement('div');
      const toLabel = document.createElement('label');
      toLabel.className = 'input-label';
      toLabel.textContent = localizeWithFallback(this.localizationService, 'LanguageService::To');
      this.toInputElm = document.createElement('input');
      this.toInputElm.type = 'text';
      this.toInputElm.inputMode = 'decimal';
      this.toInputElm.placeholder = localizeWithFallback(this.localizationService, 'LanguageService::EnterNumber');
      this.toInputElm.className = 'form-control to-input';

      this.addNumericInputFilter(this.toInputElm);

      toContainer.appendChild(toLabel);
      toContainer.appendChild(this.toInputElm);

      [this.fromInputElm, this.toInputElm].forEach(input => {
        input.addEventListener('input', () => {
          if (input === this.fromInputElm) {
            if (this.fromInputElm.value !== '') {
              this.toInputElm.min = this.fromInputElm.value;
            } else {
              this.toInputElm.removeAttribute('min');
            }
          } else {
            if (this.toInputElm.value !== '') {
              this.fromInputElm.max = this.toInputElm.value;
            } else {
              this.fromInputElm.removeAttribute('max');
            }
          }
          this.toggleInputFilledClass(input);
          this.updateApplyButtonState();
        });
      });

      container.appendChild(fromContainer);
      container.appendChild(toContainer);
    }
    return container;
  }

  protected addNumericInputFilter(input: HTMLInputElement): void {
    input.addEventListener('input', e => {
      const target = e.target as HTMLInputElement;
      const originalValue = target.value;
      const filteredValue = originalValue.replace(/[^0-9.-]/g, '');
      if (originalValue !== filteredValue) {
        target.value = filteredValue;
      }
    });
  }

    protected areInputsAvailable(): boolean {
    if (this.currentMode === 'equal') {
      return this.equalInputElm != null;
    } else if (this.currentMode === 'range') {
      return this.fromInputElm != null && this.toInputElm != null;
    }
    return false;
  }

  protected getValuesFromInputs(): any[] {
    if (!this.areInputsAvailable()) {
      return this.searchTerms;
    }
    return this.currentMode === 'equal' ? this.readEqualValue() : this.readRangeValues();
  }

  private readEqualValue(): number[] {
    if (!this.equalInputElm?.value) {
      return [];
    }
    const value = Number.parseFloat(this.equalInputElm.value);
    return Number.isNaN(value) ? [] : [value];
  }

  private readRangeValues(): (number | undefined)[] {
    const from = this.readOptionalNumber(this.fromInputElm);
    const to = this.readOptionalNumber(this.toInputElm);
    if (from === undefined && to === undefined) {
      return [];
    }
    if (from !== undefined && Number.isNaN(from) && to !== undefined && Number.isNaN(to)) {
      return [];
    }
    return [from, to];
  }

  private readOptionalNumber(input: HTMLInputElement | undefined): number | undefined {
    return input?.value ? Number.parseFloat(input.value) : undefined;
  }

    protected setValuesInInputs(values: any[]): void {
    if (!values || values.length === 0) {
      return;
    }

    let targetMode: string;
    
    if (values.length === 2) {
      targetMode = 'range';
    } else if (values.length === 1) {
      targetMode = 'equal';
    }

    if (this.currentMode !== targetMode) {
      this.currentMode = targetMode;
      this.updateModeSelection();
      this.updateInputsForMode();
    }

    setTimeout(() => {
      this.applyValuesToInputs(values, targetMode);
    }, 0);
  }

    private applyValuesToInputs(values: any[], mode: string): void {
    if (mode === 'equal' && values.length === 1) {
      if (this.equalInputElm && values[0] !== undefined && values[0] !== null) {
        this.equalInputElm.value = String(values[0]);
        this.toggleInputFilledClass(this.equalInputElm);
      }
    } else if (mode === 'range' && values.length === 2) {
      if (this.fromInputElm && values[0] !== undefined && values[0] !== null) {
        this.fromInputElm.value = String(values[0]);
        this.toggleInputFilledClass(this.fromInputElm);
      }
      if (this.toInputElm && values[1] !== undefined && values[1] !== null) {
        this.toInputElm.value = String(values[1]);
        this.toggleInputFilledClass(this.toInputElm);
      }
    }
    this.updateApplyButtonState();
  }

  protected updateApplyButtonState(): void {
    if (!this.applyButtonElm) {
      return;
    }

    const validation = this.currentMode === 'equal'
      ? this.validateEqualInput()
      : this.validateRangeInputs();

    this.applyButtonElm.disabled = !validation.isValid;
    if (this.errorElm) {
      this.errorElm.textContent = validation.errorMessage;
    }
  }

  private validateEqualInput(): { isValid: boolean; errorMessage: string } {
    const value = Number.parseFloat(this.equalInputElm?.value || '');
    const isValid = !Number.isNaN(value) && this.equalInputElm?.value !== '';
    let errorMessage = '';
    if (!isValid && this.equalInputElm?.value !== '') {
      errorMessage = localizeWithFallback(this.localizationService, 'LanguageService::EnterValidNumber');
    }
    return { isValid, errorMessage };
  }

  private validateRangeInputs(): { isValid: boolean; errorMessage: string } {
    const fromValue = this.fromInputElm?.value?.trim() || '';
    const toValue = this.toInputElm?.value?.trim() || '';

    if (!fromValue && !toValue) {
      return { isValid: false, errorMessage: '' };
    }
    if (!toValue) {
      return this.validateSingleBound(fromValue, 'LanguageService::FromValidNumber');
    }
    if (!fromValue) {
      return this.validateSingleBound(toValue, 'LanguageService::ToValidNumber');
    }
    return this.validateRangeBounds(fromValue, toValue);
  }

  private validateSingleBound(value: string, errorKey: string): { isValid: boolean; errorMessage: string } {
    if (Number.isNaN(Number.parseFloat(value))) {
      return {
        isValid: false,
        errorMessage: localizeWithFallback(this.localizationService, errorKey),
      };
    }
    return { isValid: true, errorMessage: '' };
  }

  private validateRangeBounds(fromValue: string, toValue: string): { isValid: boolean; errorMessage: string } {
    const from = Number.parseFloat(fromValue);
    const to = Number.parseFloat(toValue);
    if (Number.isNaN(from) || Number.isNaN(to)) {
      return {
        isValid: false,
        errorMessage: localizeWithFallback(this.localizationService, 'LanguageService::BothValidNumbers'),
      };
    }
    if (from > to) {
      return {
        isValid: false,
        errorMessage: localizeWithFallback(this.localizationService, 'LanguageService::FromToValidDate'),
      };
    }
    return { isValid: true, errorMessage: '' };
  }

  protected onModeChanged(): void {
    this.updateApplyButtonState();
  }

    protected formatDisplayValue(values: any[]): string {
    let result = '';

    if (!values || values.length === 0) {
      this.toggleHeaderFilledClass(false);
      return '';
    }

    if (this.currentMode === 'equal' && values.length === 1) {
      result = `= ${values[0]}`;
    } else if (this.currentMode === 'range' && values.length === 2) {
      const [from, to] = values;
      if (from !== undefined && from !== null && to !== undefined && to !== null) {
        result = `${from} - ${to}`;
      } else if (from !== undefined && from !== null) {
        result = `>= ${from}`;
      } else if (to !== undefined && to !== null) {
        result = `<= ${to}`;
      }
    }

    this.toggleHeaderFilledClass(result.trim() !== '');
    return result;
  }

    protected getOperatorForMode(mode: string): OperatorType {
    return mode === 'equal' ? OperatorType.equal : OperatorType.rangeInclusive;
  }

  private toggleInputFilledClass(input: HTMLInputElement): void {
    if (input.value.trim() !== '') {
      input.classList.add('filled');
    } else {
      input.classList.remove('filled');
    }
  }

  private toggleHeaderFilledClass(isFilled: boolean): void {
    const headerInput = this.headerInputElm;
    if (!headerInput) return;

    if (isFilled) {
      headerInput.classList.add('filled');
    } else {
      headerInput.classList.remove('filled');
    }
  }
}
